import { z } from 'zod';

type Leaf = string | number | bigint | boolean | symbol | Date;

export type PartialNullableDeep<T> = T extends Leaf | null | undefined
  ? T | null | undefined
  : T extends Array<infer ArrayType>
    ? Array<PartialNullableDeep<ArrayType>>
    : T extends ReadonlyArray<infer ArrayType>
      ? ReadonlyArray<ArrayType>
      : {
          [K in keyof T]?: PartialNullableDeep<T[K]>;
        };

// What a form sends for a question not answered yet: '' from an empty input,
// '-' from a select left at its "nothing chosen" option (the repo's
// convention). A complete document rejects both - a required string is
// `.min(1, ...)`, '' is not a number, '-' is not a cancer status - and with
// them the whole draft, so a half-filled form could not be autosaved
// (PPL-4275).
const UNANSWERED = z.enum(['', '-']);

export type Unanswered = z.output<typeof UNANSWERED>;

// PartialNullableDeep, where any answer - a number or a date too - may also be
// Unanswered. Only a value can be: a missing section is null, never ''.
export type FormDraft<T> = T extends null | undefined
  ? T
  : T extends Leaf
    ? T | Unanswered | null | undefined
    : T extends Array<infer ArrayType>
      ? Array<FormDraft<ArrayType>>
      : T extends ReadonlyArray<infer ArrayType>
        ? ReadonlyArray<ArrayType>
        : {
            [K in keyof T]?: FormDraft<T[K]>;
          };

// AI can return 'null' as a string instead of null
const nullTextToNull = (value: unknown) => (value === 'null' ? null : value);

// the value itself first, so one it accepts keeps its own parsing (a date
// string still becomes a Date). One it rejects keeps its own message: a value
// failing both options ('abc' for a number) would otherwise read as the
// union's "Invalid input", so report the value schema's first issue.
const orUnanswered = (schema: z.ZodType) =>
  z.union([schema, UNANSWERED], {
    error: (issue) =>
      issue.code === 'invalid_union'
        ? issue.errors[0]?.[0]?.message
        : undefined,
  });

const deepen = (schema: z.ZodType, isDraft: boolean): z.ZodType => {
  if (schema instanceof z.ZodObject) {
    const partial = Object.fromEntries(
      Object.entries(schema.shape as Record<string, z.ZodType>).map(
        // every field of an answer may be missing, at every depth
        ([key, field]) => [key, deepen(field, isDraft).nullish()],
      ),
    );

    return z.object(partial);
  }

  if (schema instanceof z.ZodArray) {
    return z.array(deepen(schema.element as z.ZodType, isDraft).nullish());
  }

  if (
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodDefault
  ) {
    return deepen(schema.unwrap() as z.ZodType, isDraft);
  }

  return z.preprocess(
    nullTextToNull,
    (isDraft ? orUnanswered(schema) : schema).nullish(),
  );
};

/**
 * Every value of `schema`, at every depth, made optional and nullable - what
 * an AI-generated answer is: whatever the model managed to fill in, with the
 * rest missing (and 'null' spelled as text where it meant nothing).
 *
 * The value ITSELF stays as it is - a caller says whether the whole section may
 * be missing, with `.nullish()` at the field.
 */
export const deepPartialOrNullable = <T extends z.ZodType>(schema: T) =>
  deepen(schema, false) as unknown as z.ZodType<
    PartialNullableDeep<z.output<T>>
  >;

/**
 * `schema` as a form is autosaved while it is filled in: everything
 * deepPartialOrNullable accepts, and a question not answered yet as the form
 * sends it - '' or '-' (see UNANSWERED) - in place of any value. An answer
 * that is given is still parsed and checked by its own schema.
 */
export const formDraftOf = <T extends z.ZodType>(schema: T) =>
  deepen(schema, true) as unknown as z.ZodType<FormDraft<z.output<T>>>;
